import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import Dashboard from './pages/Dashboard';
import Platform from './pages/Platform';
import Users from './pages/Users';
import CreateUser from './pages/CreateUser';
import UserDetail from './pages/UserDetail';
import EditUser from './pages/EditUser';
import UserLogs from './pages/UserLogs';
import UserInstructions from './pages/UserInstructions';
import Channels from './pages/Channels';
import Models from './pages/Models';
import Routers from './pages/Routers';
import Voice from './pages/Voice';
import Tools from './pages/Tools';
import Tiers from './pages/Tiers';
import Templates from './pages/Templates';
import Coupons from './pages/Coupons';
import Eventbridge from './pages/Eventbridge';
import Api from './pages/Api';
import Files from './pages/Files';
import Heartbeat from './pages/Heartbeat';
import DataGovernance from './pages/DataGovernance';
import Broadcast from './pages/Broadcast';
import Admins from './pages/Admins';
import SnsAlarm from './pages/SnsAlarm';
import Maintenance from './pages/Maintenance';
import StorageWarnings from './pages/StorageWarnings';
import LoadTests from './pages/LoadTests';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import { apiFetch } from './api';
import usePolling from './utils/usePolling';
import { signOut as cognitoSignOut } from './cognito';
import { Icon } from './components/ui';
import Menu, { MenuItem, MenuLabel, MenuSeparator } from './components/ui/Menu';
import Topbar from './components/shell/Topbar';
import CommandPalette from './components/shell/CommandPalette';
import ChangePasswordModal from './components/shell/ChangePasswordModal';
import { CrumbContext } from './components/shell/crumbs';
import { useTheme } from './theme';

/* ── Navigation and access, in one place ──────────────────────────────────────
   One list drives the sidebar link, the mounted route, the breadcrumb and the
   command palette entry, so none of them can disagree about who may see what.
   Two problems came from having the link and the route as separate lists:

   1. ACCESS. The role check existed only on the link, so an `admin` who typed
      /broadcast, /admins, /data-governance, /events, /api, /sns-alarm or
      /maintenance got the page rendered. The backend refused the data calls, so
      this was never a data leak, but the operator saw a console they have no
      business in and read its 403s as breakage. The same `need` now drives
      every surface.

   2. ORDER AND GROUPING. Route order did not match sidebar order, and "Other"
      held 8 of 15 links. Five sections now, none over six items:
        (unlabelled)   Dashboard, Platform, Users: where you start
        Proxies        Channels, Models, Routers, Voice, Tools: what the agent talks through
        Config         Templates, Tiers, Coupons, Files, Heartbeat: what a user gets
        Integrations   Events, API: how a producer drives us
        Operations     Broadcast, Maintenance, Load tests, Data governance, SNS alarm, Admins
      (Load tests went into Operations by Mark's choice, 2026-10-17, making it six.)

   `icon` names an entry in components/ui/Icon.jsx.

   GATED_PAGES are shown to the role that may see them even where the environment
   has not turned them on, grayed out and not a link, so an operator can tell
   "not here" from "not for you". Each names the status call that says which. */

const RANK = { analyst: 0, admin: 1, superadmin: 2 };

// An UNKNOWN role resolves to admin, deliberately. That is what the old
// `role !== 'analyst'` checks did, and `admin_role` comes from localStorage, so
// failing closed here would lock out anyone whose stored value drifts from these
// three spellings. The backend is the real gate; this decides what to render.
const can = (role, need) => (RANK[role] ?? RANK.admin) >= RANK[need];

const NAV_SECTIONS = [
  { label: null, items: [
    { to: '/', label: 'Dashboard', icon: 'dashboard', need: 'analyst' },
    // Analysts read these two as well, and change nothing: the backend's analyst guard
    // (api/analyst_guard.py) allows the GETs behind them and refuses every other request,
    // and Users and the user pages hide their controls from the analyst role.
    { to: '/platform', label: 'Platform', icon: 'infrastructure', need: 'analyst' },
    { to: '/users', label: 'Users', icon: 'users', need: 'analyst' },
  ] },
  { label: 'Proxies', items: [
    { to: '/channels', label: 'Channels', icon: 'channels', need: 'admin' },
    { to: '/models', label: 'Models', icon: 'models', need: 'admin' },
    { to: '/routers', label: 'Routers', icon: 'routers', need: 'admin' },
    { to: '/voice', label: 'Voice', icon: 'voice', need: 'admin' },
    { to: '/tools', label: 'Tools', icon: 'tools', need: 'admin' },
  ] },
  { label: 'Config', items: [
    { to: '/templates', label: 'Templates', icon: 'templates', need: 'admin' },
    { to: '/tiers', label: 'Tiers', icon: 'tiers', need: 'admin' },
    { to: '/coupons', label: 'Coupons', icon: 'coupons', need: 'admin' },
    { to: '/files', label: 'Files', icon: 'files', need: 'admin' },
    // Readable by any admin; its changes are superadmin only, server-side and in the page.
    { to: '/heartbeat', label: 'Heartbeat', icon: 'heartbeat', need: 'admin' },
  ] },
  { label: 'Integrations', items: [
    { to: '/events', label: 'Events', icon: 'events', need: 'superadmin' },
    { to: '/api', label: 'API', icon: 'api', need: 'superadmin' },
  ] },
  { label: 'Operations', items: [
    { to: '/broadcast', label: 'Broadcast', icon: 'broadcast', need: 'superadmin' },
    { to: '/maintenance', label: 'Maintenance', icon: 'maintenance', need: 'superadmin' },
    { to: '/load-tests', label: 'Load tests', icon: 'gauge', need: 'superadmin' },
    { to: '/storage-warnings', label: 'Storage warnings', icon: 'disk', need: 'superadmin' },
    { to: '/data-governance', label: 'Data governance', icon: 'governance', need: 'superadmin' },
    { to: '/sns-alarm', label: 'SNS alarm', icon: 'alarm', need: 'superadmin' },
    { to: '/admins', label: 'Admins', icon: 'admins', need: 'superadmin' },
  ] },
];

// Extra words each page answers to in the command palette. Kept out of
// NAV_SECTIONS so that list stays a flat, one-line-per-page table.
const NAV_KEYWORDS = {
  '/': ['stats', 'overview', 'analytics', 'usage', 'costs'],
  '/platform': ['infrastructure', 'architecture', 'cpu', 'memory', 'replicas', 'ecs', 'eks',
    'aurora', 'database', 'alarms', 'deployments', 'health'],
  '/users': ['agents', 'accounts', 'customers'],
  '/channels': ['telegram', 'website', 'teams', 'app', 'bots'],
  '/models': ['llm', 'providers', 'bedrock'],
  '/routers': ['router', 'routing', 'candidates', 'jev'],
  '/voice': ['speech', 's2s', 'realtime', 'azure'],
  '/tools': ['skills', 'rossgate', 'mcp', 'web search', 'internet access', 'text to speech'],
  '/templates': ['prompts', 'soul', 'messages'],
  '/tiers': ['plans', 'quotas', 'limits'],
  '/coupons': ['codes', 'promotions', 'activation'],
  '/files': ['uploads', 'documents'],
  '/heartbeat': ['check-in', 'checkin', 'proactive', 'freshness', 'engagement'],
  '/events': ['eventbridge', 'listeners', 'webhooks'],
  '/api': ['keys', 'tokens', 'interface external'],
  '/broadcast': ['announcements', 'messages', 'send'],
  '/maintenance': ['freeze', 'downtime', 'update all'],
  '/load-tests': ['load', 'stress', 'benchmark', 'performance', 'latency', 'capacity'],
  '/storage-warnings': ['storage', 'quota', 'cap', 'workspace', 'disk', 'efs', 'warning'],
  '/data-governance': ['retention', 'gdpr', 'purge'],
  '/sns-alarm': ['alerts', 'notifications'],
  '/admins': ['operators', 'roles', 'access'],
};

// Pages with no sidebar entry, reached from the Users table and from each other.
// Listed here rather than inline in <Routes> so that every route in the app
// carries a `need` and none can be added without one. An analyst opens a user
// and their logs; creating, editing and the agent's system prompt are admin pages.
const DEEP_ROUTES = [
  { to: '/users/create', element: <CreateUser />, need: 'admin' },
  { to: '/users/:userId', element: <UserDetail />, need: 'analyst' },
  { to: '/users/:userId/edit', element: <EditUser />, need: 'admin' },
  { to: '/users/:userId/logs', element: <UserLogs />, need: 'analyst' },
  { to: '/users/:userId/instructions', element: <UserInstructions />, need: 'admin' },
];

const PAGE_FOR = {
  '/': <Dashboard />, '/platform': <Platform />, '/users': <Users />, '/channels': <Channels />,
  '/models': <Models />, '/routers': <Routers />, '/voice': <Voice />,
  '/tools': <Tools />, '/templates': <Templates />,
  '/tiers': <Tiers />, '/coupons': <Coupons />, '/files': <Files />,
  '/heartbeat': <Heartbeat />,
  '/events': <Eventbridge />, '/api': <Api />, '/broadcast': <Broadcast />,
  '/maintenance': <Maintenance />, '/load-tests': <LoadTests />,
  '/storage-warnings': <StorageWarnings />,
  '/data-governance': <DataGovernance />,
  '/sns-alarm': <SnsAlarm />, '/admins': <Admins />,
};

// Pages that only work where the environment turns them on, and the call that says
// whether it has: `{enabled: bool}`. Off, the entry is grayed in the sidebar and left
// out of the palette; the page itself still renders and explains why.
const GATED_PAGES = {
  '/load-tests': '/api/load-tests/status',
  '/storage-warnings': '/api/storage-warnings/status',
};

// Derived, so route order follows sidebar order and cannot drift from it. The
// deep routes go FIRST: react-router v6 ranks static segments above dynamic ones,
// so this is belt-and-braces, but /users/create before /users/:userId makes the
// intent readable.
const ROUTES = [
  ...DEEP_ROUTES.map(r => ({ path: r.to, element: r.element, need: r.need })),
  ...NAV_SECTIONS.flatMap(s => s.items).map(i => ({
    path: i.to, element: PAGE_FOR[i.to], need: i.need,
  })),
];

const NAV_INDEX = NAV_SECTIONS.flatMap(s => s.items.map(i => ({ ...i, section: s.label })));

const SUB_LABEL = { edit: 'Edit', logs: 'Logs', instructions: 'Instructions' };

// Breadcrumbs from the URL. `detail` is the human name a user page registered
// through useCrumbLabel(), when it has one.
function crumbsFor(pathname, detail) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'users' && parts.length > 1) {
    const out = [{ label: 'Users', to: '/users' }];
    if (parts[1] === 'create') return [...out, { label: 'New user' }];
    out.push({ label: detail || `User ${parts[1]}`, to: `/users/${parts[1]}` });
    if (parts[2]) out.push({ label: SUB_LABEL[parts[2]] || parts[2] });
    return out;
  }
  const item = NAV_INDEX.find(i => i.to === pathname)
    || NAV_INDEX.filter(i => i.to !== '/' && pathname.startsWith(i.to))
      .sort((a, b) => b.to.length - a.to.length)[0];
  if (!item) return [{ label: 'Dashboard' }];
  return item.section ? [{ label: item.section }, { label: item.label }] : [{ label: item.label }];
}

function initials(name) {
  const clean = String(name || '').replace(/[^a-zA-Z0-9 ._-]/g, '').trim();
  if (!clean) return '?';
  const bits = clean.split(/[ ._-]+/).filter(Boolean);
  return ((bits[0]?.[0] || '') + (bits[1]?.[0] || bits[0]?.[1] || '')).toUpperCase();
}

const THEME_CHOICES = [
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'system', label: 'System', icon: 'monitor' },
];

function SidebarUserMenu({ role, collapsed, onToggleCollapsed, onChangePassword }) {
  const { pref, setPref } = useTheme();
  const username = localStorage.getItem('admin_username') || 'Admin';
  const email = localStorage.getItem('admin_email') || `${username.toLowerCase()}@admin.local`;

  const handleLogout = () => {
    // signOut() invalidates the refresh token Cognito-side AND clears the
    // amazon-cognito-identity-js localStorage cache, so the next login is not
    // poisoned by leftover SRP state.
    cognitoSignOut();
    window.location.reload();
  };

  return (
    <div className="sidebar-footer">
      <Menu
        label="Account"
        buttonClassName="sidebar-user"
        placement={collapsed ? 'right-end' : 'top-start'}
        matchWidth={!collapsed}
        trigger={(
          <>
            <span className="avatar" aria-hidden="true">{initials(username)}</span>
            <span className="sidebar-user-details">
              <span className="sidebar-user-name">{username}</span>
              <span className="sidebar-user-email">{email}</span>
            </span>
            <Icon name="chevronsUpDown" className="sidebar-user-chevron" />
          </>
        )}
      >
        <MenuLabel>Signed in as {role || 'admin'}</MenuLabel>
        <MenuItem icon="lock" onSelect={onChangePassword}>Change password</MenuItem>
        <MenuItem icon="panelLeft" onSelect={onToggleCollapsed} end="[">
          {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        </MenuItem>
        <MenuSeparator />
        <MenuLabel>Theme</MenuLabel>
        {THEME_CHOICES.map(t => (
          <MenuItem key={t.value} icon={t.icon} onSelect={() => setPref(t.value)}
                    end={pref === t.value ? <Icon name="check" /> : null}>
            {t.label}
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem icon="logout" onSelect={handleLogout}>Sign out</MenuItem>
      </Menu>
    </div>
  );
}

function Sidebar({ stats, mobileOpen, onClose, role, collapsed, onToggleCollapsed, onChangePassword,
                   disabledPaths }) {
  const location = useLocation();
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-backdrop" onClick={onClose} aria-hidden="true" />}
      <aside className={`sidebar${mobileOpen ? ' sidebar-open' : ''}`} aria-label="Sidebar">
        <div className="sidebar-brand">
          <Link to="/" className="sidebar-brand-link" aria-label="Ross admin home">
            <span className="ross-wordmark ross-wordmark--sidebar" aria-hidden="true">
              <span className="prefix">&gt;&nbsp;</span><span className="letters">ROSS</span><span className="underscore">_</span>
            </span>
            <span className="sidebar-subtitle">
              Admin
              <span className="sidebar-subtitle-sub">Multitenant console</span>
            </span>
          </Link>
        </div>

        {/* Sections whose every item is above this role disappear entirely, LABEL
            INCLUDED — which is what the old code got wrong for an analyst, who saw a
            bare "Proxies" heading over nothing. */}
        <nav className="sidebar-nav" aria-label="Main">
          {NAV_SECTIONS.map(section => {
            const visible = section.items.filter(i => can(role, i.need));
            if (visible.length === 0) return null;
            return (
              <React.Fragment key={section.label || 'top'}>
                {section.label && <div className="sidebar-section-label">{section.label}</div>}
                {visible.map(item => (disabledPaths && disabledPaths.has(item.to) ? (
                  <span
                    key={item.to}
                    className="sidebar-link is-disabled"
                    aria-disabled="true"
                    data-label={`${item.label} (not enabled here)`}
                    title="Not enabled on this environment"
                  >
                    <Icon name={item.icon} size={18} />
                    <span className="sidebar-link-label">{item.label}</span>
                  </span>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`sidebar-link ${isActive(item.to) ? 'active' : ''}`}
                    aria-current={isActive(item.to) ? 'page' : undefined}
                    data-label={item.label}
                  >
                    <Icon name={item.icon} size={18} />
                    <span className="sidebar-link-label">{item.label}</span>
                  </Link>
                )))}
              </React.Fragment>
            );
          })}
        </nav>

        {stats && (
          <div className="sidebar-stats" aria-label="Platform right now">
            <div className="sidebar-stat">
              <span className="sidebar-stat-value">
                <span className="dot" aria-hidden="true" />
                {stats.running_containers ?? 0}
              </span>
              <span className="sidebar-stat-label">Agents running</span>
            </div>
            <div className="sidebar-stat">
              <span className="sidebar-stat-value">{stats.total_users ?? 0}</span>
              <span className="sidebar-stat-label">Users</span>
            </div>
          </div>
        )}

        <SidebarUserMenu role={role} collapsed={collapsed}
                         onToggleCollapsed={onToggleCollapsed} onChangePassword={onChangePassword} />
      </aside>
    </>
  );
}

const COLLAPSE_KEY = 'ross-sidebar';

function AppContent({ stats, role }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [crumbDetail, setCrumbDetail] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === 'collapsed'; } catch (_) { return false; }
  });
  const { cycle: cycleTheme } = useTheme();
  const location = useLocation();
  const mainRef = useRef(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? 'collapsed' : 'expanded'); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  // Navigation: close the drawer, and start the new page at the top. `.main-content`
  // is the scroll container and it persists across routes, so without the reset a
  // page opened from halfway down a long one opened halfway down too.
  useEffect(() => {
    setMobileOpen(false);
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  const crumbs = useMemo(() => crumbsFor(location.pathname, crumbDetail), [location.pathname, crumbDetail]);

  useEffect(() => {
    const leaf = crumbs[crumbs.length - 1]?.label;
    document.title = leaf ? `${leaf} · Ross Admin` : 'Ross Admin';
  }, [crumbs]);

  // ⌘K / Ctrl+K anywhere; `[` toggles the rail when focus is not in a field.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(o => !o);
        return;
      }
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (!typing && e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey) toggleCollapsed();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleCollapsed]);

  // Which gated pages this environment has not turned on. Asked once per session, and
  // only by a role that would see them; a failed ask leaves the entry enabled, since the
  // page says itself what is on.
  const [disabledPaths, setDisabledPaths] = useState(() => new Set());
  useEffect(() => {
    const gated = Object.entries(GATED_PAGES)
      .filter(([path]) => NAV_INDEX.some(i => i.to === path && can(role, i.need)));
    if (!gated.length) return undefined;
    let alive = true;
    Promise.all(gated.map(async ([path, url]) => {
      try {
        const r = await apiFetch(url);
        if (!r.ok) return null;
        const body = await r.json();
        return body && body.enabled === false ? path : null;
      } catch (_) {
        return null;
      }
    })).then(off => { if (alive) setDisabledPaths(new Set(off.filter(Boolean))); });
    return () => { alive = false; };
  }, [role]);

  const palettePages = useMemo(() => NAV_INDEX
    .filter(i => can(role, i.need) && !disabledPaths.has(i.to))
    .map(i => ({ id: i.to, to: i.to, label: i.label, icon: i.icon, section: i.section,
                 keywords: NAV_KEYWORDS[i.to] || [] })), [role, disabledPaths]);

  const paletteActions = useMemo(() => [
    ...(can(role, 'admin') ? [{ id: 'new-user', label: 'Create a new user', icon: 'userPlus', to: '/users/create' }] : []),
    { id: 'theme', label: 'Switch theme', icon: 'moon', run: cycleTheme },
    { id: 'rail', label: collapsed ? 'Expand sidebar' : 'Collapse sidebar', icon: 'panelLeft', run: toggleCollapsed },
    { id: 'password', label: 'Change password', icon: 'lock', run: () => setChangePwOpen(true) },
    { id: 'signout', label: 'Sign out', icon: 'logout', run: () => { cognitoSignOut(); window.location.reload(); } },
  ], [role, collapsed, cycleTheme, toggleCollapsed]);

  return (
    <CrumbContext.Provider value={setCrumbDetail}>
      <div className={`app-layout${collapsed ? ' is-collapsed' : ''}`}>
        <a href="#main-content" className="skip-link">Skip to content</a>
        <Sidebar stats={stats} mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} role={role}
                 collapsed={collapsed} onToggleCollapsed={toggleCollapsed}
                 onChangePassword={() => setChangePwOpen(true)} disabledPaths={disabledPaths} />
        <div className="main-shell">
          <Topbar crumbs={crumbs} onOpenMenu={() => setMobileOpen(true)}
                  onOpenSearch={() => setPaletteOpen(true)} />
          <main className="main-content" id="main-content" ref={mainRef} tabIndex={-1}>
            {/* Only the routes this role may reach are MOUNTED. The `need` is the same
                value that decides the sidebar link, so a page can no longer be hidden
                from the nav and still render on a hand-typed URL.

                The catch-all serves double duty: anything this role cannot reach falls
                through to it alongside anything that does not exist, and both land on
                the dashboard.

                This is a UI decision about what is worth rendering, NOT the access
                control. Every one of these pages' data calls is gated server-side. */}
            <Routes>
              {ROUTES.filter(r => can(role, r.need)).map(r => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)}
                        pages={palettePages} actions={paletteActions}
                        canSearchUsers={can(role, 'analyst')} />
        <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      </div>
    </CrumbContext.Provider>
  );
}

function App() {
  const [stats, setStats] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [role, setRole] = useState(localStorage.getItem('admin_role') || null);

  // Reactive logout: cognito.js/api.js dispatch `ross:auth-expired` when the
  // session becomes unrecoverable (30-day refresh token expiry, persistent 401,
  // corrupt token cache). Flip back to the Login view in place instead of relying
  // on a full-page redirect that may not fire. Without this an expired session
  // could strand the user in the authenticated shell with a dead token.
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setRole(null);
      setStats(null);
    };
    window.addEventListener('ross:auth-expired', onExpired);
    return () => window.removeEventListener('ross:auth-expired', onExpired);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiFetch('/api/stats');
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);
  // Signed in only, and only while the tab is visible (utils/usePolling).
  usePolling(fetchStats, token ? 30000 : 0, [token]);

  // Login + ForgotPassword need <Link to=...> + useNavigate, which require a
  // Router context. Mount the Router unconditionally and split routes by auth
  // state: unauthenticated => /forgot-password + everything-else=Login;
  // authenticated => the full app shell.
  return (
    <Router>
      <div className="App">
        {token ? (
          <AppContent stats={stats} role={role} />
        ) : (
          <Routes>
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="*"
              element={<Login onLogin={(t, r) => { setToken(t); setRole(r); }} />}
            />
          </Routes>
        )}
        <ToastContainer
          position="bottom-right"
          autoClose={3500}
          newestOnTop
          closeOnClick
          pauseOnHover
          limit={4}
        />
      </div>
    </Router>
  );
}

export default App;
